'use client';

import { useEffect, useState } from 'react';
import { cn } from '../utils/cn';
import Input from './Input';
import type {
  TreeSelectionOption,
  TreeSelectionValue,
} from '../types/TreeOptionType';

type SelectTreeOptionsProps = {
  options: TreeSelectionOption[];
  value?: TreeSelectionValue;
  onValueChange?: (value: string, textValue: string) => void;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  disabled?: boolean;
  className?: string;
  name?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // Translation function for validation messages
  t?: (
    key: string,
    options?: { count?: number; plural?: string; plural_verb?: string }
  ) => string;
} & React.HTMLAttributes<HTMLDivElement>;

// SVG Components for reuse
const CircleIcon = ({
  isSelected,
  isInvalid,
}: {
  isSelected: boolean;
  isInvalid?: boolean;
}) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="block"
  >
    <rect
      x="0.5"
      y="0.5"
      width="15"
      height="15"
      rx="7.5"
      stroke={isInvalid ? '#FF4242' : '#D9D9D9'}
      strokeWidth="1"
    />
    {isSelected && (
      <circle cx="8" cy="8" r="6" fill={isSelected ? '#34BBEB' : ''} />
    )}
  </svg>
);

export default function SelectTreeOptions({
  options,
  value,
  onValueChange,
  onValidationChange,
  name,
  disabled = false,
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  onChange: fieldOnChange,
  onBlur: fieldOnBlur,
  t,
  ...rest
}: SelectTreeOptionsProps) {
  const [selected, setSelected] = useState<TreeSelectionValue>({
    key: value?.key || '',
    textValue: value?.textValue || '',
  });

  // Sync internal state when external value prop changes
  useEffect(() => {
    if (value) {
      setSelected((prev) => {
        if (
          prev.key === (value.key || '') &&
          prev.textValue === (value.textValue || '')
        ) {
          return prev;
        }
        return {
          key: value.key || '',
          textValue: value.textValue || '',
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.key, value?.textValue]);

  // Run validation whenever selections change
  useEffect(() => {
    if (onValidationChange) {
      const errors: string[] = [];
      options.forEach((option) => {
        const isOptionSelected = selected.key === option.key;
        const optionTextValue = selected.textValue || '';
        if (
          isOptionSelected &&
          option.hasTextField &&
          (!optionTextValue || optionTextValue.trim() === '')
        ) {
          errors.push(
            `${option.label} ${
              t ? t('fields.description_required') : 'requires a description'
            }`
          );
        }
      });
      onValidationChange(errors.length === 0, errors);
    }
  }, [selected, onValidationChange, options, t]);

  const handleOptionToggle = (optionKey: string) => {
    if (disabled) return;

    // If clicking on already selected option, deselect it
    if (optionKey === selected.key) {
      const newSelected = {
        key: '',
        textValue: '',
      };
      setSelected(newSelected);
      onValueChange?.('', '');

      if (typeof fieldOnChange === 'function') {
        fieldOnChange({
          target: {
            name,
            type: 'radio',
            value: '',
            checked: false,
          },
        } as React.ChangeEvent<HTMLInputElement>);
      }
      return;
    }

    const newSelected = {
      key: optionKey,
      textValue: '',
    };

    // If deselecting, newSelections remains empty (no options selected)

    setSelected(newSelected);
    // Immediate call for option selection (radio button clicks)
    onValueChange?.(newSelected.key, newSelected.textValue);

    // Notify react-hook-form
    if (typeof fieldOnChange === 'function') {
      fieldOnChange({
        target: {
          name,
          type: 'radio',
          value: optionKey,
          checked: true,
        },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleTextChange = (optionKey: string, textValue: string) => {
    if (disabled) return;

    const newSelected = {
      key: optionKey,
      textValue: textValue,
    };

    setSelected(newSelected);
    onValueChange?.(newSelected.key, newSelected.textValue);
  };

  return (
    <div className={cn('', className)} {...rest}>
      {/* All options displayed as single level */}
      <div className="flex flex-col gap-2.5 ">
        {options.map((option) => {
          const optionKey = option.key;

          const isOptionSelected = selected.key === optionKey;
          const optionInputId = `${name ?? 'tree'}-${optionKey}`;
          const optionTextValue = isOptionSelected
            ? selected.textValue || ''
            : '';

          // Check if option is invalid (selected with hasTextField but no text value)
          const isOptionInvalid =
            isOptionSelected &&
            option.hasTextField &&
            (!optionTextValue || optionTextValue.trim() === '');

          return (
            <div key={option.key} className="">
              <div className="flex flex-col gap-2.5 ">
                <div className="grid grid-cols-[20px_1fr] items-start gap-2 min-w-0">
                  {/* Hidden real input for form handling */}
                  <input
                    id={optionInputId}
                    type="radio"
                    name={`${name}-options`}
                    value={optionKey}
                    checked={isOptionSelected}
                    disabled={disabled}
                    onChange={() => handleOptionToggle(optionKey)}
                    onBlur={fieldOnBlur}
                    className="sr-only absolute opacity-0 pointer-events-none"
                  />

                  {/* Custom SVG radio button */}
                  <button
                    type="button"
                    onClick={() => handleOptionToggle(optionKey)}
                    disabled={disabled}
                    className={cn(
                      'flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-el-blue-500 rounded-full',
                      disabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:scale-105 transition-transform'
                    )}
                  >
                    <CircleIcon
                      isSelected={isOptionSelected}
                      isInvalid={isOptionInvalid}
                    />
                  </button>

                  <label
                    htmlFor={optionInputId}
                    className={cn(
                      'text-14 font-medium cursor-pointer',
                      disabled && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {option.label}
                  </label>
                </div>

                {/* Text field for option if it has one and is selected */}
                {isOptionSelected && option.hasTextField && (
                  <div className="ml-6">
                    <Input
                      type="text"
                      placeholder={
                        option.textFieldPlaceholder ||
                        (t
                          ? t('fields.default_text_placeholder')
                          : 'Enter details...')
                      }
                      value={optionTextValue}
                      onChange={(e) =>
                        handleTextChange(optionKey, e.target.value)
                      }
                      disabled={disabled}
                      className={cn(
                        'text-12  box-border',
                        isOptionInvalid &&
                          'border-red-500 focus:border-red-500 focus:ring-red-500'
                      )}
                    />
                    {isOptionInvalid && (
                      <p className="text-12 text-el-red-500">
                        {t
                          ? t('fields.field_required')
                          : 'This field is required'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
